import React, { useEffect, useState } from 'react';
import Script from 'next/script';
import { cn } from '@virginexperiencedays/tailwind';

declare global {
  interface Window {
    KlarnaOnsiteService: any[];
  }
}

// declare custom element 'klarna-placement'
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'klarna-placement': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

type KlarnaProps = {
  className?: string;
  compact?: boolean;
  price: number;
};

export const SCROLL_DELAY_SECONDS = 1.5;

const Klarna = ({ price, compact, className }: KlarnaProps) => {
  const [mountKlarna, setMountKlarna] = useState<boolean>(false);

  // Refresh Klarna
  useEffect(() => {
    window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
    window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
  }, [price]);

  // Mount Klarna script on scroll or after SCROLL_DELAY_SECONDS
  useEffect(() => {
    // Break if already mounted
    if (mountKlarna) return;

    const scrollListener = () => setMountKlarna(true);

    window.addEventListener('scroll', scrollListener);

    const mountTimer = setTimeout(() => {
      setMountKlarna(true);
    }, 1000 * SCROLL_DELAY_SECONDS);

    return () => {
      window.removeEventListener('scroll', scrollListener);
      clearTimeout(mountTimer);
    };
  }, [mountKlarna]);

  return (
    <>
      {!mountKlarna && (
        <Script
          strategy="lazyOnload"
          data-testid="klarna-script"
          src="https://js.klarna.com/web-sdk/v1/klarna.js"
          data-client-id={process.env.NEXT_PUBLIC_KLARNA_CLIENT_ID}
        />
      )}
      <div
        className={cn(
          !mountKlarna && (compact ? styles.base.compact : styles.base._),
          !mountKlarna && styles.placeholder,
          !mountKlarna && styles.animatePulse,
          className
        )}
      >
        <div
          data-testid="klarna"
          className={cn(
            !mountKlarna && 'hidden',
            compact ? styles.base.compact : styles.base._,
            styles.wrapper._,
            compact && styles.wrapper.compact,
            styles.animateFadeIn,
            className
          )}
        >
          <klarna-placement
            id="klarna-placement"
            data-key="credit-promotion-auto-size"
            data-locale="en-GB"
            data-purchase-amount={Math.round(price * 100)}
          ></klarna-placement>
        </div>
      </div>
    </>
  );
};

const styles = {
  base: {
    _: 'mt-4 min-h-[95px] [@media(min-width:320px)]:min-h-[78px] [@media(min-width:380px)]:min-h-[65px]',
    compact: 'min-h-10',
  },
  animatePulse: 'animate-[fade-in_1s_ease-in-out_infinite_alternate]',
  animateFadeIn: 'animate-[fade-in_0.5s_ease-in-out_forwards]',
  placeholder: 'w-full rounded-lg bg-background-neutral-faded',
  wrapper: {
    _: cn(
      'opacity-0',
      '[&_#klarna-placement::part(osm-container)]:rounded-lg [&_#klarna-placement::part(osm-container)]:border [&_#klarna-placement::part(osm-container)]:border-background-disabled [&_#klarna-placement::part(osm-container)]:bg-background-page [&_#klarna-placement::part(osm-container)]:p-2 [&_#klarna-placement::part(osm-container)]:text-left',
      '[&_#klarna-placement::part(osm-message)]:font-inter [&_#klarna-placement::part(osm-message)]:text-center [&_#klarna-placement::part(osm-message)]:text-sm [&_#klarna-placement::part(osm-message)]:font-normal [&_#klarna-placement::part(osm-message)]:leading-5 [&_#klarna-placement::part(osm-message)]:text-neutral',
      '[&_#klarna-placement::part(osm-logo)]:font-["Klarna_Headline"] [&_#klarna-placement::part(osm-logo)]:text-sm [&_#klarna-placement::part(osm-logo)]:font-semibold [&_#klarna-placement::part(osm-logo)]:leading-snug [&_#klarna-placement::part(osm-logo)]:text-neutral',
      '[&_#klarna-placement::part(osm-cta)]:font-inter [&_#klarna-placement::part(osm-cta)]:text-sm [&_#klarna-placement::part(osm-cta)]:font-normal [&_#klarna-placement::part(osm-cta)]:leading-5 [&_#klarna-placement::part(osm-cta)]:text-neutral',
      '[&_#klarna-placement::part(osm-legal)]:font-inter [&_#klarna-placement::part(osm-legal)]:text-center [&_#klarna-placement::part(osm-legal)]:text-neutral-faded [&_#klarna-placement::part(osm-legal)]:text-sm [&_#klarna-placement::part(osm-legal)]:font-semibold [&_#klarna-placement::part(osm-legal)]:leading-snug [&_#klarna-placement::part(osm-legal)]:mt-2 [&_#klarna-placement::part(osm-legal)]:font-normal'
    ),
    compact: cn(
      '[&_#klarna-placement::part(osm-container)]:rounded-none [&_#klarna-placement::part(osm-container)]:border-0 [&_#klarna-placement::part(osm-container)]:px-10 [&_#klarna-placement::part(osm-container)]:py-0',
      '[&_#klarna-placement::part(osm-message)]:text-center [&_#klarna-placement::part(osm-message)]:text-sm [&_#klarna-placement::part(osm-message)]:leading-5 [&_#klarna-placement::part(osm-message)]:font-normal',
      '[&_#klarna-placement::part(osm-logo)]:font-["Klarna_Headline"] [&_#klarna-placement::part(osm-logo)]:leading-5',
      '[&_#klarna-placement::part(osm-cta)]:font-normal ',
      '[&_#klarna-placement::part(osm-legal)]:hidden'
    ),
  },
};

export default Klarna;
